import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, User, FileText, Mic, BookOpen, BarChart3, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useReducedMotion from '../../hooks/useReducedMotion';
import { cardHover, staggerContainer, maybe } from '../../lib/motionVariants';

const ITEMS = [
  {
    key: 'certificates',
    icon: Award,
    to: '/certificates',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    color: 'text-amber-600',
    hoverBg: 'hover:bg-amber-100',
  },
  {
    key: 'profile',
    icon: User,
    to: '/profile',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    color: 'text-cyan-600',
    hoverBg: 'hover:bg-cyan-100',
  },
  {
    key: 'documents',
    icon: FileText,
    to: '/documents',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    color: 'text-purple-600',
    hoverBg: 'hover:bg-purple-100',
  },
  {
    key: 'interview',
    icon: Mic,
    to: '/cabin-crew',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    color: 'text-rose-600',
    hoverBg: 'hover:bg-rose-100',
  },
  {
    key: 'courses',
    icon: BookOpen,
    to: '/courses',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    color: 'text-indigo-600',
    hoverBg: 'hover:bg-indigo-100',
  },
  {
    key: 'progress',
    icon: BarChart3,
    // /progress/:courseId requires a real enrollment — falls back to
    // /my-courses when there isn't one yet (see courseId prop below).
    to: (courseId) => (courseId ? `/progress/${courseId}` : '/my-courses'),
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    color: 'text-teal-600',
    hoverBg: 'hover:bg-teal-100',
  },
];

// Sidebar variant ("Hızlı Erişim" — Panel redesign): Sertifikalar / Sınavlarım /
// İlerlemem / Kurslar. NOTE: mockup şows a 4th "Topluluk" (community) item —
// no community/forum page exists in this app yet, substituted with "Kurslar"
// (courses catalog) so there's no dead link. Flag this to PM/UX if a real
// community feature is planned.
const SIDEBAR_KEYS = ['certificates', 'progress', 'courses'];

/**
 * QuickAccessGrid — 2x3 grid of quick navigation links.
 *
 * Props:
 *   variant  — "grid" (default) | "sidebar"
 *              "grid" renders the full 2x3 grid with card-duo wrapper
 *              "sidebar" renders 4 items in a compact vertical list (no outer wrapper)
 *   courseId — primary enrollment's course_id, used to build the "progress" link
 */
export default function QuickAccessGrid({ variant = 'grid', courseId = null }) {
  const { t } = useLanguage();
  const reduced = useReducedMotion();

  if (variant === 'sidebar') {
    const sidebarItems = ITEMS.filter(item => SIDEBAR_KEYS.includes(item.key));
    return (
      <nav
        aria-label={t('dashboard.quickAccess')}
        data-testid="quick-access-sidebar"
        className="card-duo p-[18px]"
      >
        <h3 className="font-duo-display text-[15px] text-[#0E1F3D] mb-3">
          {t('dashboard.quickAccess')}
        </h3>
        <ul className="flex flex-col gap-2">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const href = typeof item.to === 'function' ? item.to(courseId) : item.to;
            return (
              <li key={item.key}>
                <Link
                  to={href}
                  className="flex items-center gap-3 rounded-[10px] border border-[#edf1ee] bg-white hover:bg-[#f3faf5] hover:border-[#34CC67] px-3.5 py-2.5 transition-colors group"
                  aria-label={t(`dashboard.quickAccess.${item.key}`)}
                  data-testid={`quick-access-sidebar-${item.key}`}
                >
                  <Icon
                    size={18}
                    strokeWidth={2}
                    className={`${item.color} shrink-0`}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold text-[#0E1F3D]">
                    {t(`dashboard.quickAccess.${item.key}`)}
                  </span>
                  <ChevronRight size={16} strokeWidth={2.5} className="ml-auto text-[#b7c2ba]" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }

  // Default: full grid variant
  return (
    <section
      className="card-duo p-5"
      aria-labelledby="quick-access-heading"
      data-testid="quick-access-grid"
    >
      <h2
        id="quick-access-heading"
        className="font-duo-display text-base text-[var(--text-primary)] mb-4"
      >
        {t('dashboard.quickAccess')}
      </h2>

      <nav aria-label={t('dashboard.quickAccess')}>
        <motion.ul
          className="grid grid-cols-3 gap-3"
          variants={maybe(reduced, staggerContainer(0.05, 0.10))}
          initial="hidden"
          animate="visible"
        >
          {ITEMS.map((item) => {
            const Icon = item.icon;
            const href = typeof item.to === 'function' ? item.to(courseId) : item.to;
            return (
              <motion.li
                key={item.key}
                variants={maybe(reduced, cardHover)}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
              >
                <Link
                  to={href}
                  className={`flex flex-col items-center gap-1.5 rounded-xl border-2 ${item.border} ${item.bg} ${item.hoverBg} p-3 transition-colors group`}
                  aria-label={t(`dashboard.quickAccess.${item.key}`)}
                  data-testid={`quick-access-${item.key}`}
                >
                  <Icon
                    size={22}
                    strokeWidth={2}
                    className={`${item.color} group-hover:scale-110 transition-transform`}
                    aria-hidden="true"
                  />
                  <span className="text-[11px] font-bold text-[var(--text-primary)] text-center leading-tight">
                    {t(`dashboard.quickAccess.${item.key}`)}
                  </span>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>
      </nav>
    </section>
  );
}
